import { Chain } from '@1delta/chain-registry'
import {
  bitfiFetcher,
  bitfiYieldKey,
} from '../../yields/intrinsic/fetchers/bitfi'
import type { SavingsRegistryEntry } from './registry'

/**
 * BitFi rows — see BITFI.md at the repo root for the assessment.
 *
 * **These are not LSTs, and the distinction is the whole reason they live
 * here.** BitFi's own strategy page names the yield source as positive funding
 * rates on perpetual swaps and spot-versus-derivative basis spreads, run by
 * third-party quantitative teams against assets custodied at Ceffu and mirrored
 * onto exchanges through MirrorX; staking rewards appear only as an optional
 * add-on "where available". That is the Ethena / Maple / Native trust class,
 * not the Lido one — and only a savings row can SAY so, because `solvency`
 * exists on `SavingsRegistryEntry` and not on `LstShareToken`. It is left
 * ABSENT on every row below: the collateral sits in exchange and custodian
 * accounts, there is no on-chain invariant tying a share to anything, and a
 * comfortable `overcollateralized` would be a fabrication.
 *
 * They are nonetheless `yield-bearing` rather than `volatile`, and that was
 * measured rather than assumed: across 416 settled bfBTC epochs the ratio fell
 * in 6, and the deepest drawdown from a peak was **0.229 %** (pbfUSD: 0 falls
 * in 280, hbfUSD: 1). That is nothing like the Yield Basis / HLP shape the
 * `VOLATILE_VAULT_OVERRIDES` list exists for, so no override is registered —
 * but the possibility is real and stated in the descriptions, since Pulsar's
 * own documentation permits drawdowns between epochs.
 *
 * ## Two products
 *
 * **bfBTC** is one contract that is simultaneously the token, the staking
 * vault and the price — a LayerZero OFT with `deposit`, `requestWithdraw`,
 * `previewWithdraw` and `ratio()` on it. Six deployments are registered.
 *
 * **hbfUSD / pbfUSD** are ordinary ERC-4626 vaults over bfUSD on Ethereum,
 * Horizon conservative and Pulsar levered, coupled by design: part of
 * Horizon's revenue boosts Pulsar, and Pulsar's capital plus a protocol buffer
 * absorbs losses for Horizon first. They are tiny — about $450 k of bfUSD
 * exists in total against ~$230 m of bfBTC — and are registered for
 * completeness, not for size.
 *
 * ## The `underlying` is the PRICED asset, not the deposit token
 *
 * bfBTC is denominated in BTC everywhere, but the token `deposit()` actually
 * pulls (`underlyingAsset()`) is per-chain and sometimes obscure: on Ethereum
 * it is **hemiBTC** — a bridged token with a 20.75 BTC supply — not WBTC, and
 * on Base, Core, TAC, GOAT and Pharos it is the zero address, i.e. there is no
 * ERC-20 entry at all and BTC arrives over the Bitcoin network to be credited
 * by the multisig. Pointing `underlying` at those would price most of the book
 * at zero and take every row's LTV with it (the Olympus Cooler failure). So
 * `underlying` is the chain's liquid BTC reference, which is what the row must
 * be valued in, and the action layer resolves the real deposit token live from
 * `underlyingAsset()` and refuses a mismatch. The two agree on BNB (BTCB) and
 * Hemi (hemiBTC) and diverge everywhere else.
 *
 * ## Deliberately omitted
 *
 * Core (1116), TAC (239) and GOAT (2345) carry bfBTC at the same addresses but
 * hold **0.0024, 0.0002 and 0.0011 bfBTC** — under $200 between them — publish
 * a `minDepositTokenAmount` of ~`uint256.max/100` (deposits closed), and TAC
 * and GOAT are running a ratio 3.9 % behind every other chain, so a row would
 * advertise a stale price on an unenterable vault. AILayer (2649) holds a real
 * $79.7 m per DefiLlama but BitFi publishes no address for it, the chain is
 * absent from `chainMapping.ts`, and its public RPC refuses `eth_call`.
 */

/** bfBTC, one row per chain. */
const BFBTC: {
  chain: string
  address: string
  /** The BTC token the row is PRICED in — see the note above. */
  underlying: string
  underlyingDecimals: number
  /** What `deposit()` pulls, for the description. `undefined` ⇒ no ERC-20
   *  entry exists on this chain. */
  depositToken?: string
  /** Flat per-request withdrawal fee in bfBTC, from `feeConfigs`. */
  exitNote: string
}[] = [
  {
    chain: Chain.ETHEREUM_MAINNET,
    address: '0xcdfb58c8c859cb3f62ebe9cf2767f9e036c7fb15',
    underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', // WBTC
    underlyingDecimals: 8,
    depositToken: 'hemiBTC',
    exitNote:
      'Both legs are open; the flat fee is 0.00009 bfBTC to Ethereum and 0.000012 to Bitcoin',
  },
  {
    chain: Chain.BNB_SMART_CHAIN_MAINNET,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
    underlying: '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c', // BTCB
    underlyingDecimals: 18,
    depositToken: 'BTCB',
    exitNote:
      'Both legs are open; the flat fee is 0.000006 bfBTC to BNB Chain and 0.000012 to Bitcoin',
  },
  {
    chain: Chain.BASE,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
    underlying: '0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf', // cbBTC
    underlyingDecimals: 8,
    exitNote:
      'The only exit is to Bitcoin (flat 0.000012 bfBTC) — the EVM route is switched off by a 100 % fee',
  },
  {
    chain: Chain.HEMI_NETWORK,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
    underlying: '0xaa40c0c7644e0b2b224509571e10ad20d9c4ef28', // hemiBTC
    underlyingDecimals: 8,
    depositToken: 'hemiBTC',
    exitNote:
      'The only exit is to Hemi (flat 0.00002 bfBTC) — the Bitcoin route is off, contrary to the docs',
  },
  {
    chain: Chain.BITLAYER_MAINNET,
    address: '0xcdfb58c8c859cb3f62ebe9cf2767f9e036c7fb15',
    underlying: '0xff204e2681a6fa0e2c3fade68a1b28fb90e4fc5f', // WBTC
    underlyingDecimals: 18,
    depositToken: 'the chain’s native BTC gas token',
    exitNote:
      'The only exit is to Bitlayer (flat 0.000006 bfBTC) — the Bitcoin route is off',
  },
  {
    chain: Chain.PHAROS_MAINNET,
    address: '0x623f2774d9f27b59bc6b954544487532ce79d9df',
    underlying: '0xd990cf81e9aea2417326fdfac12b19aa66f8b6c2', // FBTC
    underlyingDecimals: 8,
    exitNote:
      'The only exit is to Bitcoin (flat 0.00002 bfBTC) — the EVM route is switched off by a 100 % fee',
  },
]

const bfbtcDescription = (row: (typeof BFBTC)[number]): string =>
  `bfBTC accrues against BTC through a daily settled exchange ratio. The ` +
  `yield is not staking: it is delta-neutral derivatives trading (perpetual ` +
  `funding and basis) run by third-party quant teams on assets in Ceffu ` +
  `custody, so the backing is off-chain with no on-chain solvency invariant. ` +
  (row.depositToken
    ? `Deposits take ${row.depositToken}. `
    : `No on-chain deposit here — BTC is sent on the Bitcoin network. `) +
  `Exits need approval from a 2-of-3 BitFi Safe, above which a single EOA ` +
  `can replace the Safe, upgrade the contract and blacklist. ${row.exitNote}.`

export const BITFI_ENTRIES: { [chainId: string]: SavingsRegistryEntry[] } = {
  ...Object.fromEntries(
    BFBTC.map((row) => [
      row.chain,
      [
        {
          reader: 'bitfi-bfbtc' as const,
          address: row.address,
          underlying: row.underlying,
          symbol: 'bfBTC',
          brand: 'BitFi',
          description: bfbtcDescription(row),
          decimals: 8,
          underlyingDecimals: row.underlyingDecimals,
          isRebasing: false,
          // Every deployment that still accepts an entry accepts it
          // permissionlessly; the ones that do not are closed by an
          // unreachable minimum, which the reader publishes as
          // `depositCapacity: 0` rather than as a permission.
          isMintable: true,
          withdrawalMode: 'request-based' as const,
          // One epoch of cooldown (`cooldownEpoches()` = 1) before the
          // multisig may approve, and an epoch is ~89,580 s — not a day.
          withdrawalCooldownSeconds: 89_580,
          yieldFetcher: bitfiFetcher,
          yieldKey: bitfiYieldKey(row.chain, row.address),
        } satisfies SavingsRegistryEntry,
      ],
    ]),
  ),
}

/**
 * hbfUSD / pbfUSD — appended to Ethereum, which already carries bfBTC.
 *
 * **`redemptionFee()` reads `(0, 0)` and the exit is still not free.** The
 * claim pays the requester the snapshotted `amount` and sends the record's
 * `unvestedYield` to the protocol's fee receiver, so redeeming mid-epoch
 * forfeits the remainder of that epoch's yield. Fork-proven 2026-09-08; the
 * reader publishes the live size of it as `withdrawFeeBps`.
 *
 * The advertised targets are 9 % and 17 % APY. What the chain has actually
 * paid since launch (2026-11-03) is **3.32 % and 8.92 %**, and the trailing
 * seven epochs at integration were 0.98 % and 2.91 % — so the rate published
 * here, which is measured from `epochRatios`, will read roughly half the
 * number BitFi's own front page shows. That is the measurement, not a bug.
 */
const BFUSD_VAULTS: SavingsRegistryEntry[] = [
  {
    reader: 'bitfi-vault',
    address: '0xde5d4ab42251ba9af6f247cf07c9a4793fa6ed88',
    underlying: '0xa3eb7a9e57fca4e40b79e394ed5eb37fed205a24', // bfUSD
    symbol: 'hbfUSD',
    brand: 'BitFi',
    description:
      "Horizon is the conservative of BitFi's two bfUSD pools. bfUSD is minted 1:1 " +
      'against USDC or USDT and the pool runs delta-neutral derivatives strategies ' +
      'on assets in Ceffu custody, so the backing is off-chain with no on-chain ' +
      'solvency invariant. Part of its revenue boosts the Pulsar pool, which in ' +
      'exchange stands as first-loss capital for Horizon. A redemption is a ' +
      'request settled three epochs later and then claimed, and the protocol ' +
      "keeps the rest of that epoch's yield — so leaving early in an epoch " +
      "costs more. Deposits price at the epoch's end ratio, above the current " +
      'share price.',
    decimals: 6,
    underlyingDecimals: 6,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'request-based',
    // 3 epochs of `settlementDelay()` at ~89,580 s each.
    withdrawalCooldownSeconds: 268_740,
    yieldFetcher: bitfiFetcher,
    yieldKey: bitfiYieldKey(
      Chain.ETHEREUM_MAINNET,
      '0xde5d4ab42251ba9af6f247cf07c9a4793fa6ed88',
    ),
  },
  {
    reader: 'bitfi-vault',
    address: '0x4f85cbfdefbdb3fa96fcf1e38b0ee68db9ae438b',
    underlying: '0xa3eb7a9e57fca4e40b79e394ed5eb37fed205a24', // bfUSD
    symbol: 'pbfUSD',
    brand: 'BitFi',
    description:
      "Pulsar is the levered of BitFi's two bfUSD pools: it takes the residual of " +
      'the same delta-neutral derivatives book, is boosted by Horizon revenue, and ' +
      'in exchange is first-loss capital for Horizon — so it is designed to draw ' +
      'down between epochs. The backing sits in Ceffu custody with no on-chain ' +
      'solvency invariant. A redemption is a request settled three epochs later ' +
      "and then claimed, and the protocol keeps the rest of that epoch's yield " +
      "— so leaving early in an epoch costs more. Deposits price at the epoch's " +
      'end ratio, above the current share price.',
    decimals: 6,
    underlyingDecimals: 6,
    isRebasing: false,
    isMintable: true,
    withdrawalMode: 'request-based',
    withdrawalCooldownSeconds: 268_740,
    yieldFetcher: bitfiFetcher,
    yieldKey: bitfiYieldKey(
      Chain.ETHEREUM_MAINNET,
      '0x4f85cbfdefbdb3fa96fcf1e38b0ee68db9ae438b',
    ),
  },
]

BITFI_ENTRIES[Chain.ETHEREUM_MAINNET] = [
  ...(BITFI_ENTRIES[Chain.ETHEREUM_MAINNET] ?? []),
  ...BFUSD_VAULTS,
]
